import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import { BoundedContent, Box, Icon, IconButton, Typography, useScreenClass } from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import { sanitiseLocale } from '../../../../i18n/config';
import styles, { stylesheet } from './Gallery.module.scss';
import ImageTile from './ImageTile';
import { getIndustryLabel } from './Industries';
import type { IndustryKey } from './Industries';

const Gallery = () => {
    useStyles(stylesheet);
    const { t, i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);

    const [currentImageTile, setCurrentImageTile] = useState(0);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [isCSR, setIsCSR] = useState(false);
    const galleryRef = useRef<HTMLDivElement>(null);
    const screenClass = useScreenClass();
    const isMobile = screenClass === 'xs';

    useEffect(() => {
        const handleScroll = () => {
            if (!galleryRef.current) return;

            const rect = galleryRef.current.getBoundingClientRect();
            // Calculate offset based on how far the gallery top (rect.top)
            // is from viewport bottom (window.innerHeight),
            setScrollOffset(window.innerHeight - rect.top);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        setIsCSR(true);

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const isGermanLocale = ['de-DE', 'de-AT', 'de-CH'].includes(locale);
    const isFrenchLocale = ['fr-CA', 'fr-FR', 'fr-CH', 'fr-BE'].includes(locale);

    const germanLogoImages = [
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-1.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-1.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-1.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-2.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-2.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-2.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-3.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-3.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-3.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-4.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-4.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-4.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-5.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-5.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-5.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-6.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-6.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-6.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-7.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-7.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-7.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-8.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-8.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-8.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-9.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-9.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-9.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-10.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-10.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-10.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-11.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-11.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-11.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-12.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-12.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-12.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-13.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-13.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-13.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-14.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-14.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-14.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-15.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-15.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-15.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-16.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-16.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-16.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-17.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-17.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-17.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-18.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-18.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-18.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-19.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-19.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-19.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-20.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-20.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-20.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-21.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-21.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-21.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-22.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-22.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-22.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-23.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-23.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-23.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-24.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-24.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-24.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-de-25.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-de-25.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-de-25.altText'),
        },
    ];

    const frenchLogoImages = [
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-1.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-1.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-1.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-2.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-2.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-2.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-3.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-3.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-3.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-4.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-4.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-4.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-5.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-5.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-5.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-6.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-6.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-6.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-7.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-7.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-7.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-8.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-8.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-8.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-9.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-9.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-9.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-10.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-10.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-10.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-11.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-11.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-11.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-12.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-12.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-12.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-13.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-13.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-13.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-14.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-14.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-14.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-15.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-15.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-15.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-16.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-16.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-16.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-17.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-17.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-17.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-18.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-18.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-18.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-19.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-19.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-19.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-20.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-20.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-20.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-21.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-21.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-21.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-22.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-22.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-22.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-23.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-23.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-23.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-24.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-24.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-24.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-fr-25.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-fr-25.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-fr-25.altText'),
        },
    ];

    const defaultLogoImages = [
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-1.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-1.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-1.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-2.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-2.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-2.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-3.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-3.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-3.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-4.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-4.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-4.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-5.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-5.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-5.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-6.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-6.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-6.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-7.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-7.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-7.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-8.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-8.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-8.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-9.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-9.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-9.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-10.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-10.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-10.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-11.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-11.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-11.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-12.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-12.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-12.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-13.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-13.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-13.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-14.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-14.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-14.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-15.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-15.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-15.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-16.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-16.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-16.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-17.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-17.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-17.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-18.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-18.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-18.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-19.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-19.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-19.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-20.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-20.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-20.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-21.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-21.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-21.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-22.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-22.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-22.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-23.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-23.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-23.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-24.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-24.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-24.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-25.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-25.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-25.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-26.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-26.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-26.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-27.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-27.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-27.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-28.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-28.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-28.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-29.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-29.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-29.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-30.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-30.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-30.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-31.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-31.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-31.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-32.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-32.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-32.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-33.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-33.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-33.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-34.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-34.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-34.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-35.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-35.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-35.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-36.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-36.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-36.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-37.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-37.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-37.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-38.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-38.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-38.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-39.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-39.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-39.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-40.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-40.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-40.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-41.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-41.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-41.altText'),
        },
        {
            src: t('Logomaker GenAi Landing Page > Gallery > logo-42.src'),
            industryKey: t('Logomaker GenAi Landing Page > Gallery > logo-42.seoUrlSuffix'),
            altText: t('Logomaker GenAi Landing Page > Gallery > logo-42.altText'),
        },
    ];

    const localeSpecificLogos = isGermanLocale
        ? germanLogoImages
        : isFrenchLocale
          ? frenchLogoImages
          : null;

    const logoImages = localeSpecificLogos
        ? [...localeSpecificLogos, ...defaultLogoImages.slice(25)]
        : defaultLogoImages;

    const imageTiles = logoImages.map(({ src, altText, industryKey }) => ({
        src,
        altText,
        industryLabel: getIndustryLabel(industryKey as IndustryKey, t),
    }));

    const totalImageTiles = imageTiles.length;

    const nextTile = () => {
        setCurrentImageTile((prev) => (prev + 1) % totalImageTiles);
    };

    const previousTile = () => {
        setCurrentImageTile((prev) => (prev - 1 + totalImageTiles) % totalImageTiles);
    };

    const getTilePosition = (index: number) => {
        const current = currentImageTile;
        const prev = (current - 1 + totalImageTiles) % totalImageTiles;
        const next = (current + 1) % totalImageTiles;

        if (index === current) return 'center';
        if (index === prev) return 'left';
        if (index === next) return 'right';
        return 'hidden';
    };

    return (
        <BoundedContent
            as="section"
            backgroundColor="warm-white"
            id="gallery"
            p={0}
            aria-live={isMobile ? 'polite' : undefined}
            aria-roledescription={isMobile ? 'carousel' : undefined}
            aria-label={t('Logomaker GenAi Landing Page > Gallery > Aria Label.value')}
        >
            {/* Override default fontWeight of editorial-headline to match Figma */}
            {/* eslint-disable-next-line @vp/swan/no-mixed-font-props */}
            <Typography
                fontSkin="editorial-headline"
                fontWeight="normal"
                textAlign="center"
                py={{ xs: 8, sm: 9, md: 9, lg: 9, xl: 9 }}
                px={{ xs: 4 }}
                m={0}
                as="h2"
            >
                {t('Logomaker GenAi Landing Page > Gallery > Title.value')}
            </Typography>
            {isCSR && !isMobile ? (
                <Box className={styles.gallery} ref={galleryRef}>
                    <Box
                        className={`${styles.galleryLayout} ${styles.galleryRowOdd}`}
                        // scrollOffset * the animation speed
                        style={{ '--scroll-offset': `${scrollOffset * 0.4}px` } as CSSProperties}
                        as="ul"
                        p={0}
                        m={0}
                        mt={2}
                    >
                        {imageTiles.slice(0, 14).map((image, index) => {
                            const imageTileNum = index + 1;

                            return (
                                <Box
                                    key={index}
                                    className={styles.imageTileContainer}
                                    as="li"
                                    aria-label={t(
                                        'Logomaker GenAi Landing Page > Gallery > Image Tile > Aria Label.value',
                                        {
                                            currentNumber: imageTileNum,
                                            totalNumber: totalImageTiles,
                                        },
                                    )}
                                >
                                    <ImageTile image={image} />
                                </Box>
                            );
                        })}
                    </Box>
                    <Box
                        className={`${styles.galleryLayout} ${styles.galleryRowEven}`}
                        // scrollOffset * the animation speed
                        style={{ '--scroll-offset': `${-scrollOffset * 0.2}px` } as CSSProperties}
                        as="ul"
                        p={0}
                        mx={0}
                        my={6}
                    >
                        {imageTiles.slice(14, 28).map((image, mapIndex) => {
                            const index = mapIndex + 14;
                            const imageTileNum = index + 1;

                            return (
                                <Box
                                    key={index}
                                    className={styles.imageTileContainer}
                                    as="li"
                                    aria-label={t(
                                        'Logomaker GenAi Landing Page > Gallery > Image Tile > Aria Label.value',
                                        {
                                            currentNumber: imageTileNum,
                                            totalNumber: totalImageTiles,
                                        },
                                    )}
                                >
                                    <ImageTile image={image} />
                                </Box>
                            );
                        })}
                    </Box>
                    <Box
                        className={`${styles.galleryLayout} ${styles.galleryRowOdd}`}
                        // scrollOffset * the animation speed
                        style={{ '--scroll-offset': `${scrollOffset * 0.4}px` } as CSSProperties}
                        as="ul"
                        p={0}
                        m={0}
                        mb={2}
                    >
                        {imageTiles.slice(28, 42).map((image, mapIndex) => {
                            const index = mapIndex + 28;
                            const imageTileNum = index + 1;

                            return (
                                <Box
                                    key={index}
                                    className={styles.imageTileContainer}
                                    as="li"
                                    aria-label={t(
                                        'Logomaker GenAi Landing Page > Gallery > Image Tile > Aria Label.value',
                                        {
                                            currentNumber: imageTileNum,
                                            totalNumber: totalImageTiles,
                                        },
                                    )}
                                >
                                    <ImageTile image={image} />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            ) : (
                <Box className={styles.gallery} ref={galleryRef}>
                    <Box className={styles.galleryLayout} as="ul" padding={0} margin={0}>
                        {imageTiles.map((image, index) => {
                            const tilePosition = getTilePosition(index);
                            const imageTileNum = index + 1;

                            return (
                                <Box
                                    key={index}
                                    className={styles.imageTileContainer}
                                    data-position={tilePosition}
                                    as="li"
                                    aria-label={t(
                                        'Logomaker GenAi Landing Page > Gallery > Image Tile > Aria Label.value',
                                        {
                                            currentNumber: imageTileNum,
                                            totalNumber: totalImageTiles,
                                        },
                                    )}
                                    aria-roledescription={isMobile ? 'slide' : undefined}
                                    aria-hidden={isMobile && tilePosition === 'hidden'}
                                >
                                    <ImageTile
                                        image={image}
                                        position={tilePosition}
                                        isTabbable={!isMobile}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                    <Box className={`${styles.carouselButtonContainer} ${styles.prev}`}>
                        <IconButton
                            tooltip={t(
                                'Logomaker GenAi Landing Page > Gallery > Previous Button > Label.value',
                            )}
                            compactMode
                            onClick={previousTile}
                        >
                            <Icon iconType="chevronLeft" />
                        </IconButton>
                    </Box>
                    <Box className={`${styles.carouselButtonContainer} ${styles.next}`}>
                        <IconButton
                            tooltip={t(
                                'Logomaker GenAi Landing Page > Gallery > Next Button > Label.value',
                            )}
                            compactMode
                            onClick={nextTile}
                        >
                            <Icon iconType="chevronRight" />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </BoundedContent>
    );
};

export default Gallery;
